import type { InternalNode, Node } from "@xyflow/react";

/**
 * Geometry for "floating" edges: instead of fixed handles, the connector runs between the points
 * where the center-to-center line crosses each node's border, so it always takes the shortest
 * path no matter where nodes are dragged. Adapted from the React Flow floating-edges example.
 */
/** Border point of `node` on the line running from its center toward an arbitrary canvas point. */
export const getNodeBorderPoint = (
	node: InternalNode<Node>,
	toward: { x: number; y: number }
): { x: number; y: number } => {
	const w = (node.measured.width ?? 0) / 2;
	const h = (node.measured.height ?? 0) / 2;

	const x2 = node.internals.positionAbsolute.x + w;
	const y2 = node.internals.positionAbsolute.y + h;

	const xx1 = (toward.x - x2) / (2 * w) - (toward.y - y2) / (2 * h);
	const yy1 = (toward.x - x2) / (2 * w) + (toward.y - y2) / (2 * h);
	const a = 1 / (Math.abs(xx1) + Math.abs(yy1) || 1);
	const xx3 = a * xx1;
	const yy3 = a * yy1;

	return { x: w * (xx3 + yy3) + x2, y: h * (-xx3 + yy3) + y2 };
};

const getNodeIntersection = (
	intersectionNode: InternalNode<Node>,
	targetNode: InternalNode<Node>
): { x: number; y: number } =>
	getNodeBorderPoint(intersectionNode, {
		x: targetNode.internals.positionAbsolute.x + (targetNode.measured.width ?? 0) / 2,
		y: targetNode.internals.positionAbsolute.y + (targetNode.measured.height ?? 0) / 2
	});

/** Border-to-border endpoints of the shortest connector between two nodes. */
export const getFloatingEdgeParams = (
	source: InternalNode<Node>,
	target: InternalNode<Node>
): { sx: number; sy: number; tx: number; ty: number } => {
	const sourcePoint = getNodeIntersection(source, target);
	const targetPoint = getNodeIntersection(target, source);

	return { sx: sourcePoint.x, sy: sourcePoint.y, tx: targetPoint.x, ty: targetPoint.y };
};
